import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { faTags } from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Catalog,
  FilterStatus,
  IAuctionFilterDto,
} from '@shared/interfaces/search.interface';
import { LanguageService } from '@shared/services/language.service';
import { SearchService } from '@shared/services/search.service';
import { TagInfo } from '../../../tags-manager/interfaces/tag.interface';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { BidSearchResultComponent } from '@shared/components/bid-search-result/bid-search-result.component';
import { PlatformService } from '@shared/services/platform-service.service';
import { zip } from 'rxjs';
import { AuctionView } from '../../../articles-manager/interfaces/auction.interface';

@Component({
  selector: 'app-tags-regular',
  templateUrl: './regular.component.html',
  styleUrls: ['./regular.component.scss'],
  standalone: true,
  imports: [FaIconComponent, BidSearchResultComponent, TranslatePipe],
})
@UntilDestroy()
export class RegularComponent implements OnInit {
  faTags = faTags;

  isLoaded = signal<boolean>(false);
  idAuctionList = signal<Array<number>>([]);
  memorabidAuctions = signal<Array<AuctionView>>([]);
  marketplaceAuctions = signal<Array<AuctionView>>([]);
  idAuctionType = signal<FilterStatus>(-1);

  tag = input<TagInfo>();
  metaEventSlice = input<number>();

  languageService = inject(LanguageService);

  public auctionDownloadedService = inject(AuctionDownloadedService);
  private readonly searchService = inject(SearchService);
  public platformService = inject(PlatformService);
  private readonly route = inject(ActivatedRoute);

  tagTranslated = computed(
    () =>
      this.tag().tagTranslatedList.find(
        (tag) =>
          tag.idLanguage ===
          this.languageService.userSelectedLanguage.idLanguage
      ) ?? this.tag().tagTranslatedList?.[0]
  );
  tagNameToShow = computed(() => this.tagTranslated().translatedValue);
  logoFileNameView = computed(() => this.tag().logoFileNameView);
  title = computed(() => this.tagTranslated().title);
  description = computed(() => this.tagTranslated().description);

  @Output('updateMeta') updateMetaEvent = new EventEmitter<{
    memorabidAuctions: AuctionView[];
    marketplaceAuctions: AuctionView[];
  }>();

  constructor() {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.idAuctionType.set(+params['auctionStatus'] || -1);
    });

    this.loadData(this.buildFilter());
  }

  // onAuctionStatusFilterChanged(data: { auctionStatusFilter: FilterStatus }) {
  // TODO: fix types
  onAuctionStatusFilterChanged(data: any) {
    this.loadData(
      this.buildFilter({
        auctionStatus: data.auctionStatusFilter,
      }),
      false
    );
  }

  private loadData(
    filters: IAuctionFilterDto = this.buildFilter(),
    restorePosition = true
  ) {
    this.isLoaded.set(false);

    this.searchService.setFilters(this.searchService.searchRoute, {
      ...filters,
    });

    const apiCalls = [
      this.searchService
        .getAllV2({
          ...filters,
          idAuctionType: this.searchService.auctionTypeList[1]?.key,
        })
        .pipe(untilDestroyed(this)),
      this.searchService
        .getAllV2({
          ...filters,
          idAuctionType: this.searchService.auctionTypeList[2]?.key,
        })
        .pipe(untilDestroyed(this)),
    ];

    this.searchService
      .getAllTypes()
      .pipe(untilDestroyed(this))
      .subscribe((dataR) => {
        this.searchService.auctionTypeList = dataR;

        zip<Array<AuctionView[]>>(apiCalls).subscribe(
          ([memorabidData, marketplaceData]: Array<AuctionView[]>) => {
            const filteredMemorabidData = memorabidData
              .filter((a: AuctionView) => !a.isFinished)
              .map((a: AuctionView) => a.idAuction);

            const filteredMarketplaceData = marketplaceData
              .filter((a: AuctionView) => !a.isFinished)
              .map((a: AuctionView) => a.idAuction);

            this.idAuctionList.set([
              ...filteredMemorabidData,
              ...filteredMarketplaceData,
            ]);
            this.memorabidAuctions.set(
              (this.auctionDownloadedService.memorabidAuctions = memorabidData)
            );
            this.marketplaceAuctions.set(
              (this.auctionDownloadedService.marketplaceAuctions =
                marketplaceData)
            );

            this.isLoaded.set(true);
            this.updateMetaEvent.emit({
              memorabidAuctions: this.memorabidAuctions().slice(
                this.metaEventSlice()
              ),
              marketplaceAuctions: this.marketplaceAuctions().slice(
                this.metaEventSlice()
              ),
            });

            if (restorePosition) {
              this.platformService.runOnBrowser(() => {
                setTimeout(() => {
                  this.auctionDownloadedService.restoreScrollPosition(
                    'tags',
                    'auto'
                  );
                }, 200);
              });
            }
          }
        );
      });
  }

  private buildFilter(
    filters: Partial<IAuctionFilterDto> = {}
  ): IAuctionFilterDto {
    return {
      idLanguage: this.languageService.userSelectedLanguage.idLanguage,
      searchText: '',
      idAuctionType: -1,
      idDetail: null,
      onlySigned: false,
      idSport: null,
      idCompetition: null,
      idTeam: null,
      onlyNew: false,
      onlyExpiring: false,
      onlyArriving: false,
      onlyBuyNow: false,
      auctionStatus: this.idAuctionType(),
      idEntity: null,
      idCampaign: null,
      idPromoter: null,
      idActiveAuction: null,
      idTagList: [this.tag().idTag],
      orderBy: 'evidence',
      orderDirection: 'desc',
      catalog: Catalog.All,
      ...filters,
    };
  }
}
